"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrcadInstanceScopeSuffixRule = void 0;
const NetNamingRules_1 = require("../../../../NetCombobulation/NetNamingRules");
const LogicalSchema_1 = require("../../../parsers/dsn/LogicalSchema");
const OrcadDsnPortOccurrenceUtils_1 = require("../Generators/OrcadDsnPortOccurrenceUtils");
class OrcadInstanceScopeSuffixRule {
    constructor() {
        this.type = NetNamingRules_1.NetNamingRuleType.Finalize;
        this.suffixableGroupBaseNames = new Map();
        this.reservedDesignWideNames = new Set();
    }
    apply(groups, hierarchy) {
        this.recordNewGroupsFromHierarchy(groups, hierarchy);
        this.resetTrackedGroupsToBaseNames();
        this.applyConflictSuffixes();
    }
    recordNewGroupsFromHierarchy(groups, hierarchy) {
        const eligible = new Set();
        for (const group of groups) {
            if (!group.physicalName) {
                continue;
            }
            if (this.isDesignWideUnifyingNamed(group)) {
                this.reservedDesignWideNames.add(group.physicalName.toUpperCase());
                continue;
            }
            eligible.add(group);
        }
        const visit = (node) => {
            for (const group of eligible) {
                if (!this.suffixableGroupBaseNames.has(group) && group.getNets().has(node.instance)) {
                    this.suffixableGroupBaseNames.set(group, group.physicalName ?? '');
                }
            }
            for (const child of node.children ?? []) {
                visit(child);
            }
        };
        visit(hierarchy);
    }
    resetTrackedGroupsToBaseNames() {
        for (const [group, baseName] of this.suffixableGroupBaseNames) {
            group.physicalName = baseName;
        }
    }
    applyConflictSuffixes() {
        const buckets = new Map();
        for (const [group, baseName] of this.suffixableGroupBaseNames) {
            let bucket = buckets.get(baseName);
            if (!bucket) {
                bucket = { hierarchicalPort: [], other: [] };
                buckets.set(baseName, bucket);
            }
            if (group.hasLabelType('hierarchical-port') && !this.isAliasNamedNotPortNamed(group)) {
                bucket.hierarchicalPort.push(group);
            }
            else {
                bucket.other.push(group);
            }
        }
        for (const [baseName, { hierarchicalPort, other }] of buckets) {
            const collidesWithReservedGlobal = this.reservedDesignWideNames.has(baseName.toUpperCase());
            this.suffixCollidingGroups(hierarchicalPort, collidesWithReservedGlobal);
            this.suffixCollidingGroups(other, false);
        }
    }
    suffixCollidingGroups(collidingGroups, collidesWithReservedGlobal) {
        if (collidingGroups.length < 2 && !collidesWithReservedGlobal) {
            return;
        }
        const plan = collidingGroups.map((group) => {
            const suffixed = this.plannedSuffixedName(group);
            return {
                group,
                finalName: suffixed ?? group.physicalName ?? '',
                wasSuffixed: suffixed !== undefined && suffixed !== group.physicalName,
            };
        });
        const entriesByFinalName = new Map();
        for (const entry of plan) {
            const list = entriesByFinalName.get(entry.finalName) ?? [];
            list.push(entry);
            entriesByFinalName.set(entry.finalName, list);
        }
        for (const [finalName, entries] of entriesByFinalName) {
            if (entries.length === 1) {
                const [entry] = entries;
                if (entry.wasSuffixed) {
                    entry.group.physicalName = entry.finalName;
                }
                continue;
            }
            const recordIds = entries.map((entry) => this.occurrenceNetSystemId(entry.group));
            if (recordIds.some((id) => id === undefined) || new Set(recordIds).size !== recordIds.length) {
                continue;
            }
            if (!this.groupsConfinedToDisjointOccurrenceBranches(entries.map((entry) => entry.group))) {
                continue;
            }
            const smallestRecordId = Math.min(...recordIds);
            for (const [index, entry] of entries.entries()) {
                const recordId = recordIds[index];
                entry.group.physicalName = recordId === smallestRecordId ? finalName : `${finalName}_${recordId}`;
            }
        }
    }
    groupsConfinedToDisjointOccurrenceBranches(groups) {
        const branches = groups.map((group) => this.confinedOccurrenceBranch(group));
        for (const branch of branches) {
            for (const other of branches) {
                if (branch === other || !branch || !other) {
                    continue;
                }
                if (other.branchIds.has(branch.deepestId)) {
                    return false;
                }
            }
        }
        return true;
    }
    confinedOccurrenceBranch(group) {
        const subdesignInstances = Array.from(group.getNets().keys()).filter((instance) => instance.sheetSymbol);
        if (subdesignInstances.length === 0) {
            return undefined;
        }
        let deepest = subdesignInstances[0];
        for (const instance of subdesignInstances) {
            if (instance.path.length > deepest.path.length) {
                deepest = instance;
            }
        }
        const branchIds = new Set([deepest.pathUniqueId]);
        for (const ancestor of deepest.path) {
            if (ancestor.sheetSymbol) {
                branchIds.add(ancestor.pathUniqueId);
            }
        }
        return { deepestId: deepest.pathUniqueId, branchIds };
    }
    occurrenceNetSystemId(group) {
        const base = (this.suffixableGroupBaseNames.get(group) ?? group.physicalName ?? '').toUpperCase();
        const scopeSuffix = this.confinedOccurrenceScopeSuffix(group)?.toUpperCase();
        const rawName = scopeSuffix && base.endsWith(`_${scopeSuffix}`) ? base.slice(0, -(scopeSuffix.length + 1)) : base;
        const prefix = LogicalSchema_1.OrCadDSN.OCCURRENCE_NET_SYSTEM_ID_PROP_PREFIX;
        for (const instance of group.getNets().keys()) {
            const props = instance.instOccurence?.UserProperty;
            if (!props) {
                continue;
            }
            const prop = props.find((entry) => entry.Name === `${prefix}${rawName}` || entry.Name === `${prefix}${base}`);
            if (!prop) {
                continue;
            }
            const id = Number.parseInt(prop.Value, 10);
            if (!Number.isNaN(id)) {
                return id;
            }
        }
        return undefined;
    }
    plannedSuffixedName(group) {
        if (this.isCrossBoundaryNamed(group)) {
            return undefined;
        }
        const suffix = this.confinedOccurrenceScopeSuffix(group);
        if (!suffix || !group.physicalName) {
            return undefined;
        }
        if (group.physicalName.toUpperCase().endsWith(`_${suffix}`)) {
            return group.physicalName;
        }
        return `${group.physicalName}_${suffix}`;
    }
    confinedOccurrenceScopeSuffix(group) {
        if (this.isAliasNamedNotPortNamed(group)) {
            const ownsRootScopedNet = Array.from(group.getNets().keys()).some((instance) => !instance.sheetSymbol);
            if (ownsRootScopedNet) {
                return undefined;
            }
        }
        const subdesignInstances = Array.from(group.getNets().keys()).filter((instance) => instance.sheetSymbol);
        if (subdesignInstances.length === 0) {
            return undefined;
        }
        const distinctSheetSymbols = new Set(subdesignInstances.map((instance) => instance.sheetSymbol));
        if (distinctSheetSymbols.size !== 1) {
            return undefined;
        }
        let deepest = subdesignInstances[0];
        for (const instance of subdesignInstances) {
            if (instance.path.length > deepest.path.length) {
                deepest = instance;
            }
        }
        const branchIds = new Set([deepest.pathUniqueId, ...deepest.path.map((p) => p.pathUniqueId)]);
        for (const instance of subdesignInstances) {
            if (!branchIds.has(instance.pathUniqueId)) {
                return undefined;
            }
        }
        const chain = group.hasLabelType('hierarchical-port') ? [deepest] : [...deepest.path, deepest];
        const parts = [];
        for (const instance of chain) {
            if (instance.sheetSymbol) {
                parts.push(instance.sheetSymbol.Name.toUpperCase());
            }
        }
        if (parts.length === 0) {
            return undefined;
        }
        return parts.join('_');
    }
    isAliasNamedNotPortNamed(group) {
        const base = group.physicalName;
        if (!base) {
            return false;
        }
        const upperBase = base.toUpperCase();
        let hasMatchingAlias = false;
        for (const label of group.getNetLabels()) {
            if (label.originalLabel.toUpperCase() !== upperBase) {
                continue;
            }
            if (label.labelType === 'hierarchical-port' || label.labelType === 'virtual-bus-member') {
                return false;
            }
            if (label.labelType === 'alias') {
                hasMatchingAlias = true;
            }
        }
        return hasMatchingAlias;
    }
    isCrossBoundaryNamed(group) {
        return this.isDesignWideUnifyingNamed(group) || this.crossesOccurrenceBoundary(group);
    }
    crossesOccurrenceBoundary(group) {
        const parentKeys = new Set();
        const childKeys = new Set();
        for (const label of group.getNetLabels()) {
            const occurrence = label.strategyMetadata?.['port-occurrence'];
            if (!occurrence) {
                continue;
            }
            if (label.labelType === 'hierarchical-sheet-entry') {
                parentKeys.add((0, OrcadDsnPortOccurrenceUtils_1.portOccurrenceIdentityKey)(occurrence));
            }
            else if (label.labelType === 'hierarchical-port') {
                childKeys.add((0, OrcadDsnPortOccurrenceUtils_1.portOccurrenceIdentityKey)(occurrence));
            }
        }
        return Array.from(parentKeys).some((key) => childKeys.has(key));
    }
    isDesignWideUnifyingNamed(group) {
        if (group.hasLabelType('global')) {
            return true;
        }
        for (const [, nets] of group.getNets()) {
            for (const net of nets) {
                for (const conn of net.connections) {
                    if (conn.connection.appliesNetName?.labelType === 'global') {
                        return true;
                    }
                }
            }
        }
        return false;
    }
}
exports.OrcadInstanceScopeSuffixRule = OrcadInstanceScopeSuffixRule;
//# sourceMappingURL=OrcadInstanceScopeSuffixRule.js.map